
import React, { forwardRef } from 'react';
import { ChartTitle, ChartGrid, ChartLines, ChartAttribution } from './ChartComponents';

interface ChartSVGProps {
  rounds: Array<{
    proximityScore?: number;
    userWord?: string;
    aiWord?: string;
  }>;
  width: number;
  height: number;
  padding: number;
  isConverged: boolean;
  gameType: string;
  date: string;
  actualFinalWord: string;
  convergenceRound: number | null;
}

const ChartSVG = forwardRef<SVGSVGElement, ChartSVGProps>(
  ({ rounds, width, height, padding, isConverged, gameType, date, actualFinalWord, convergenceRound }, ref) => {
    // Chart dimensions
    const maxRounds = 20;
    
    // Fixed width for mobile consistency - always 320px
    const fixedWidth = 320; // Fixed at 320px
    
    // Apply reduced padding all around
    const effectivePadding = padding * 0.75; // Reduce padding by 25%
    
    // Text positioning with consistent spacing
    const titleFontSize = 18;
    const subtitleFontSize = 14;
    const textLineHeight = 14;
    
    // Properly space elements vertically
    const topSpacing = 20; // Top margin
    const textSpacing = 28; // Space between text lines
    const elemSpacing = 20; // Space between major elements
    
    // Position text elements with proper spacing
    const textStartY = topSpacing + titleFontSize; // First line of text
    const textLine2Y = textStartY + textSpacing; // Second line of text
    
    // Chart starts after text with proper spacing
    const chartStartY = textLine2Y + elemSpacing;
    
    // We'll use the smaller dimension for both width and height to create a square
    const innerRectOriginalWidth = fixedWidth - (effectivePadding * 2);
    
    // Use scale factor of 1 (no scaling)
    const scaleFactor = 1; // No scaling
    const innerRectWidth = innerRectOriginalWidth * scaleFactor;
    const innerRectHeight = innerRectWidth; // Make it a perfect square
    
    // Center the inner rectangle in the fixed-width SVG
    const innerRectX = ((fixedWidth - innerRectWidth) / 2);
    const innerRectY = chartStartY;

    // Add inner padding for spacing between border and first/last lines
    const innerPadding = 4; // Small padding inside rectangle
    
    // Calculate row height based on available chart height minus inner padding
    const rowHeight = (innerRectHeight - (innerPadding * 2)) / maxRounds;

    // Position the website text below the chart with appropriate spacing
    const websiteTextY = chartStartY + innerRectHeight + elemSpacing;
    const websiteTextFontSize = subtitleFontSize;
    
    // Calculate new total height to fit all elements with spacing at the bottom
    const bottomPadding = elemSpacing; // Padding at the bottom
    const newHeight = websiteTextY + websiteTextFontSize + bottomPadding;

    // Get user's final word
    const lastRound = rounds.length > 0 ? rounds[rounds.length - 1] : null;
    const userFinalWord = lastRound?.userWord || actualFinalWord || "";

    // Font family to use throughout the SVG
    const fontFamily = "'Inter', 'Helvetica', 'Arial', sans-serif";

    return (
      <svg 
        ref={ref} 
        width={fixedWidth} 
        height={newHeight} 
        viewBox={`0 0 ${fixedWidth} ${newHeight}`} 
        xmlns="http://www.w3.org/2000/svg" 
        className="bg-background mx-auto"
        style={{ fontFamily, minWidth: fixedWidth }}
      >
        {/* SVG styles for font embedding */}
        <defs>
          <style>
            {`
              @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap');
              text {
                font-family: ${fontFamily};
              }
            `}
          </style>
        </defs>
        
        {/* Chart background */}
        <rect x="0" y="0" width={fixedWidth} height={newHeight} fill="#1A1A1A" data-fill-variable="--background" />
        
        {/* Game info text at top of SVG */}
        <ChartTitle 
          userWord={userFinalWord}
          date={date}
          roundsLength={rounds.length}
          width={fixedWidth}
          textStartY={textStartY}
          textLine2Y={textLine2Y}
          titleFontSize={titleFontSize}
          subtitleFontSize={subtitleFontSize}
        />
        
        {/* Chart border */}
        <ChartGrid 
          innerRectX={innerRectX}
          innerRectY={innerRectY}
          innerRectWidth={innerRectWidth}
          innerRectHeight={innerRectHeight}
        />
        
        {/* Render lines for each round */}
        <ChartLines 
          rounds={rounds}
          maxRounds={maxRounds}
          innerRectX={innerRectX}
          innerRectY={innerRectY}
          innerRectWidth={innerRectWidth}
          innerPadding={innerPadding}
          rowHeight={rowHeight}
          fixedWidth={fixedWidth}
        />

        {/* Website attribution text */}
        <ChartAttribution 
          width={fixedWidth}
          websiteTextY={websiteTextY}
          websiteTextFontSize={websiteTextFontSize}
        />
      </svg>
    );
  }
);

ChartSVG.displayName = 'ChartSVG';
export default ChartSVG;
